import styled from 'styled-components'
import { type Modal2Props } from '@niaratech/niara-react-components/lib/controls/Modal2'
import { scrollbarStyle } from '@niaratech/niara-react-components/src/util/scrollbarStyle'
import { readableColor } from 'polished'
import { BREAKPOINT_MD, BREAKPOINT_SM } from '../../theme/ota'
import { ModalOTAProps } from './ModalOTA'

type Variant = Modal2Props['mobileVariant']

interface Props {
  mobileVariant?: Variant
  tabletVariant?: Variant
  desktopVariant?: Variant
  sidePosition?: 'left' | 'right'
  hasTitle?: boolean
  headerSize?: string
  zIndex?: number
  transitionReverse?: boolean
  padding?: string
  margin?: string
  maxWidth?: string
  paddingZero?: boolean
  hasGallery?: boolean
  stickyHeader?: boolean
  titleFontSize?: ModalOTAProps['titleFontSize']
  backgroundColor?: string
}

const _mediaQueryForStyle =
  (variant: Variant) =>
  ({ mobileVariant = 'modal', tabletVariant, desktopVariant = 'modal' }: Props) => {
    if (!tabletVariant) tabletVariant = desktopVariant
    if (mobileVariant === variant && tabletVariant === variant && desktopVariant === variant) {
      return 'all'
    } else if (mobileVariant === variant && mobileVariant === tabletVariant) {
      return `(max-width: calc(calc(${BREAKPOINT_MD}) - 1px))`
    } else if (mobileVariant === variant) {
      return `(max-width: calc(calc(${BREAKPOINT_SM}) - 1px))`
    } else if (tabletVariant === variant && tabletVariant === desktopVariant) {
      return `(min-width: calc(${BREAKPOINT_SM}))`
    } else if (tabletVariant === variant) {
      return `(min-width: calc(${BREAKPOINT_SM})) and (max-width: calc(calc(${BREAKPOINT_SM}) - 1px))`
    } else if (desktopVariant === variant) {
      return `(min-width: calc(${BREAKPOINT_MD}))`
    } else {
      return 'not all'
    }
  }

type ContainerProps = {
  backgroundColor?: string
  mobileVariant?: Variant
}
export const Container = styled.div<ContainerProps>`
  position: relative;
  color: ${({ backgroundColor }) => (backgroundColor ? readableColor(backgroundColor) : 'unset')};
  background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : 'unset')} !important;

  @media (max-width: calc(${BREAKPOINT_SM})) {
    height: ${({ mobileVariant }) => (['full', 'side'].includes(mobileVariant) ? '100vh !important' : 'inherit')};
  }
`

type HeaderProps = {
  padding?: string
}

export const Header = styled.div<HeaderProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray3} !important;
  padding: ${({ padding }) => padding || '20px'} !important;
`

export const Title = styled.div<Props>`
  font-weight: 700;
  font-size: ${({ titleFontSize }) => titleFontSize ?? '20px'};
  line-height: 27px;
  white-space: break-spaces;
`

export const Content = styled.div<Props>`
  height: 100%;
  overflow-y: ${({ stickyHeader }) => (stickyHeader ? 'auto' : 'unset')};
  padding: ${({ padding }) => padding || '20px'};
  background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : 'unset')} !important;

  @media ${_mediaQueryForStyle('modal')} {
    max-height: ${({ stickyHeader }) => (stickyHeader ? 'calc(100vh - 200px)' : 'unset')};
  }

  @media ${_mediaQueryForStyle('full')} {
    max-height: calc(100vh - 68px);
  }

  @media ${_mediaQueryForStyle('side')} {
    max-height: calc(100vh - 68px);
  }

  ${scrollbarStyle}
`

export const FloatingCloseButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`
